import { useEffect, useState } from 'react';

/**
 * Custom hook to determine if the current device is a touch-enabled device.
 *
 * This hook checks for touch capabilities by examining the `ontouchstart` event
 * in the window object and the touch point properties in the navigator object.
 * It updates the state whenever the window is resized to ensure the touch capability
 * status is current.
 *
 * @returns {boolean} - Returns `true` if the device is touch-enabled, otherwise `false`.
 */
const useIsTouchDevice = () => {
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  const checkTouchDevice = () =>
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0;

  useEffect(() => {
    const updateIsTouchDevice = () => {
      setIsTouchDevice(checkTouchDevice());
    };

    updateIsTouchDevice();
    window.addEventListener('resize', updateIsTouchDevice);

    return () => {
      window.removeEventListener('resize', updateIsTouchDevice);
    };
  }, []);

  return isTouchDevice;
};

export { useIsTouchDevice };
